import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import configService from '../services/configService';

const MultiObjectiveConfig = ({ onComplete }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [objectives, setObjectives] = useState({
    primary_objective: null,
    secondary_objectives: []
  });
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadMultiObjectiveData();
  }, []);

  const loadMultiObjectiveData = async () => {
    try {
      setLoading(true);
      const cognitoUserId = configService.getUserId();
      
      const [currentObjectives, templates] = await Promise.all([
        loadCurrentObjectives(cognitoUserId),
        loadAvailableTemplates()
      ]);

      setObjectives(currentObjectives || { primary_objective: null, secondary_objectives: [] });
      setAvailableTemplates(templates);
      
    } catch (error) {
      console.error('Error loading multi-objective data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentObjectives = async (cognitoUserId) => {
    try {
      const token = await configService.getAuthToken();
      const response = await fetch(`https://oauth.remote-assistant.ai/oauth/users/${cognitoUserId}/objectives`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 404) return null;
      if (!response.ok) throw new Error(`Failed to load objectives (${response.status})`);

      return await response.json();
    } catch (error) {
      console.error('Error loading objectives:', error);
      return null;
    }
  };

  const loadAvailableTemplates = async () => {
    try {
      const token = await configService.getAuthToken();
      const response = await fetch('https://oauth.remote-assistant.ai/api/templates', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to load templates (${response.status})`);
      
      const data = await response.json();
      return Object.values(data.templates || {});
    } catch (error) {
      console.error('Error loading templates:', error);
      return [];
    }
  };

  const handlePrimaryObjectiveSelect = (template) => {
    setObjectives(prev => ({
      ...prev,
      primary_objective: {
        id: template.id,
        goal: template.goal,
        required_capabilities: template.required_capabilities,
        success_criteria: template.success_criteria,
        priority: 'primary',
        weight: 0.7
      }
    }));
  };

  const handleSecondaryObjectiveAdd = (template) => {
    const newSecondary = {
      id: template.id,
      goal: template.goal,
      required_capabilities: template.required_capabilities,
      success_criteria: template.success_criteria,
      priority: 'secondary',
      weight: 0.3 / (objectives.secondary_objectives.length + 1),
      conditions: ['after_hours', 'high_volume']
    };

    setObjectives(prev => ({
      ...prev,
      secondary_objectives: [...prev.secondary_objectives, newSecondary]
    }));
  };

  const handleSecondaryObjectiveRemove = (index) => {
    setObjectives(prev => ({
      ...prev,
      secondary_objectives: prev.secondary_objectives.filter((_, i) => i !== index)
    }));
  };

  const handleWeightChange = (type, index, weight) => {
    if (type === 'primary') {
      setObjectives(prev => ({
        ...prev,
        primary_objective: { ...prev.primary_objective, weight: parseFloat(weight) }
      }));
    } else {
      setObjectives(prev => ({
        ...prev,
        secondary_objectives: prev.secondary_objectives.map((obj, i) => 
          i === index ? { ...obj, weight: parseFloat(weight) } : obj
        )
      }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const cognitoUserId = configService.getUserId();
      const token = await configService.getAuthToken();

      const response = await fetch(`https://oauth.remote-assistant.ai/oauth/users/${cognitoUserId}/objectives`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          objective_config: {
            ...objectives,
            objective_hierarchy: {
              primary_weight: objectives.primary_objective?.weight || 0.7,
              secondary_weights: objectives.secondary_objectives.map(obj => obj.weight),
              conflict_resolution: 'priority_based'
            }
          }
        })
      });

      if (!response.ok) throw new Error(`Failed to save objectives (${response.status})`);

      if (onComplete) onComplete();
      
    } catch (error) {
      console.error('Error saving objectives:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">Loading multi-objective configuration...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Multi-Objective Configuration</h2>
        <p className="text-gray-600">Configure primary and secondary business objectives with priority weighting</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Primary Objective */}
      <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
          🎯 Primary Objective
          <span className="ml-2 text-sm font-normal text-gray-600">(Main business goal)</span>
        </h3>
        
        {objectives.primary_objective ? (
          <div className="bg-blue-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-blue-900">{objectives.primary_objective.goal}</h4>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Weight:</label>
                <input
                  type="range"
                  min="0.5"
                  max="1.0"
                  step="0.1"
                  value={objectives.primary_objective.weight}
                  onChange={(e) => handleWeightChange('primary', null, e.target.value)}
                  className="w-20"
                />
                <span className="text-sm font-medium">{Math.round(objectives.primary_objective.weight * 100)}%</span>
              </div>
            </div>
            <p className="text-sm text-blue-700">
              Success Criteria: {objectives.primary_objective.success_criteria?.join(', ')}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {availableTemplates.slice(0, 4).map((template) => (
              <div
                key={template.id}
                onClick={() => handlePrimaryObjectiveSelect(template)}
                className="border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-blue-300 hover:bg-blue-50"
              >
                <h4 className="font-medium text-gray-900">{template.display_name || template.id}</h4>
                <p className="text-sm text-gray-600">{template.goal}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Secondary Objectives */}
      <div className="bg-white border-2 border-green-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-green-800 flex items-center">
            🎯 Secondary Objectives
            <span className="ml-2 text-sm font-normal text-gray-600">(Additional goals - max 3)</span>
          </h3>
          <span className="text-sm text-gray-500">{objectives.secondary_objectives.length}/3</span>
        </div>

        {objectives.secondary_objectives.map((objective, index) => (
          <div key={index} className="bg-green-50 rounded-lg p-4 mb-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-green-900">{objective.goal}</h4>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Weight:</label>
                <input
                  type="range"
                  min="0.1"
                  max="0.5"
                  step="0.1"
                  value={objective.weight}
                  onChange={(e) => handleWeightChange('secondary', index, e.target.value)}
                  className="w-20"
                />
                <span className="text-sm font-medium">{Math.round(objective.weight * 100)}%</span>
                <button
                  onClick={() => handleSecondaryObjectiveRemove(index)}
                  className="text-red-500 hover:text-red-700 ml-2"
                >
                  ✕
                </button>
              </div>
            </div>
            <p className="text-sm text-green-700">
              Conditions: {objective.conditions?.join(', ')}
            </p>
          </div>
        ))}

        {objectives.secondary_objectives.length < 3 && (
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            {availableTemplates
              .filter(t => t.id !== objectives.primary_objective?.id)
              .filter(t => !objectives.secondary_objectives.some(s => s.id === t.id))
              .slice(0, 4)
              .map((template) => (
                <div
                  key={template.id}
                  onClick={() => handleSecondaryObjectiveAdd(template)}
                  className="border border-dashed border-gray-300 rounded-lg p-3 cursor-pointer hover:border-green-300 hover:bg-green-50"
                >
                  <h4 className="font-medium text-gray-700">+ {template.display_name || template.id}</h4>
                  <p className="text-sm text-gray-500">{template.goal}</p>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Conflict Resolution */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">⚖️ Conflict Resolution</h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-700 mb-2">
            <strong>Strategy:</strong> Priority-based resolution
          </p>
          <p className="text-sm text-gray-600">
            When objectives conflict, the system will prioritize based on weights: 
            Primary ({Math.round((objectives.primary_objective?.weight || 0.7) * 100)}%) → 
            Secondary objectives by weight
          </p>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={!objectives.primary_objective || saving}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            objectives.primary_objective && !saving
              ? 'bg-blue-500 hover:bg-blue-600 text-white'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {saving ? 'Saving...' : 'Save Multi-Objective Configuration'}
        </button>
      </div>
    </div>
  );
};

export default MultiObjectiveConfig;